import React, { useState, useEffect, useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./GroupClassSuccessStep.module.scss";
import {
  IGroupClass,
  IGroupClassInstructor,
  IGroupClassFamilyMember,
} from "../../models";
import { useAuthentication } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../../../AppContext";
import { toLatinTransliteration } from "../../../../../common/infrastructure/utilities/transliterationUtility";
import { AddToCalendarButton } from "../../../../../common/components/calendar/AddToCalendarButton";
import { NotificationPermissionDialog } from "../../../../notifications/NotificationPermissionDialog";

interface IGroupClassSuccessStepProps {
  className?: string;
  selectedClass?: IGroupClass | null;
  selectedInstructor?: IGroupClassInstructor | null;
  actualInstructorName?: string | null;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  selectedFamilyMember?: IGroupClassFamilyMember | null;
  reservationId?: number;
  onBookAnother?: () => void;
  onGoToDashboard?: () => void;
  isWaitlistBooking?: boolean;
  requirePrepayment?: boolean;
  prepaymentLink?: string | null;
  requireFormCompletion?: boolean;
  formCompletionLink?: string | null;
}

export const GroupClassSuccessStep = ({
  className,
  selectedClass,
  selectedInstructor,
  actualInstructorName,
  selectedDate,
  selectedTime,
  selectedFamilyMember,
  reservationId,
  onBookAnother,
  onGoToDashboard,
  isWaitlistBooking = false,
  requirePrepayment = false,
  prepaymentLink,
  requireFormCompletion = false,
  formCompletionLink,
}: IGroupClassSuccessStepProps) => {
  const { currentUser } = useAuthentication();
  const { selectedStudio } = useAppContext();
  const [showDetails, setShowDetails] = useState(false);
  const [celebrationActive, setCelebrationActive] = useState(false);

  // Compute payment reference with transliterated name
  const paymentReference = useMemo(() => {
    const name = selectedFamilyMember
      ? selectedFamilyMember.name
      : currentUser
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : "";

    const latinName = toLatinTransliteration(name);
    return reservationId ? `${latinName} - ${reservationId}` : latinName;
  }, [selectedFamilyMember, currentUser, reservationId]);

  // Trigger celebration animation after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setCelebrationActive(true);
    }, 500);

    const detailsTimer = setTimeout(() => {
      setShowDetails(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearTimeout(detailsTimer);
    };
  }, []);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("bg-BG", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleBookAnother = () => {
    if (onBookAnother) {
      onBookAnother();
    }
  };

  const handleGoToDashboard = () => {
    if (onGoToDashboard) {
      onGoToDashboard();
    }
  };

  // Resolve the booked instructor name. For an "any instructor" booking we keep
  // showing "Всеки инструктор"; an empty actual name means the booked class has no
  // instructor, so the instructor card is hidden.
  const instructorName =
    selectedInstructor?.id === -1
      ? actualInstructorName === ""
        ? ""
        : "Всеки инструктор"
      : selectedInstructor?.name ?? "";

  return (
    <div className={classNames(styles.container, className)}>
      {/* Success Animation */}
      <div className={styles.successAnimation}>
        <div
          className={classNames(styles.successIcon, {
            [styles.celebrating]: celebrationActive,
          })}
        >
          <span className={styles.checkmark}>✓</span>

          {/* Celebration particles */}
          {celebrationActive && (
            <>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.1s",
                    "--x": "-20px",
                    "--y": "-30px",
                  } as React.CSSProperties
                }
              >
                🎉
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.2s",
                    "--x": "25px",
                    "--y": "-25px",
                  } as React.CSSProperties
                }
              >
                ✨
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.3s",
                    "--x": "-30px",
                    "--y": "20px",
                  } as React.CSSProperties
                }
              >
                🎊
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.4s",
                    "--x": "30px",
                    "--y": "25px",
                  } as React.CSSProperties
                }
              >
                ⭐
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.5s",
                    "--x": "0px",
                    "--y": "-40px",
                  } as React.CSSProperties
                }
              >
                🎈
              </div>
            </>
          )}
        </div>

        <h1 className={styles.successTitle}>
          {isWaitlistBooking
            ? "Записахте се в списъка на чакащите! ⏳"
            : requirePrepayment
              ? "Успешна резервация! 🎉"
              : "Успешна резервация! 🎉"}
        </h1>
        <p className={styles.successSubtitle}>
          {isWaitlistBooking
            ? "Ще бъдете уведомени по имейл, когато се освободи място."
            : requirePrepayment
              ? "Ще получите имейл с инструкции за плащане."
              : "Вашата резервация е потвърдена! Ще получите имейл за потвърждение."}
        </p>
      </div>

      {/* Booking Details */}
      {showDetails && (
        <div className={styles.bookingDetails}>
          <div className={styles.bookingHeader}>
            <h2 className={styles.bookingTitle}>Детайли за резервацията</h2>
          </div>

          <div className={styles.detailsGrid}>
            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🏢</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Обект</span>
                <span className={styles.detailValue}>
                  {selectedStudio?.name}
                </span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🏋️</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Клас</span>
                <span className={styles.detailValue}>
                  {selectedClass?.className}
                </span>
              </div>
            </div>

            {instructorName && (
              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>👤</div>
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Инструктор</span>
                  <span className={styles.detailValue}>{instructorName}</span>
                </div>
              </div>
            )}

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>📅</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Дата и час</span>
                <span className={styles.detailValue}>
                  {selectedDate && formatDate(selectedDate)} в {selectedTime}
                </span>
              </div>
            </div>

            {selectedClass?.categoryName && (
              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>📂</div>
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Категория</span>
                  <span className={styles.detailValue}>
                    {selectedClass.categoryName}
                  </span>
                </div>
              </div>
            )}

            {reservationId && (
              <div
                className={classNames(styles.detailCard, styles.highlightCard)}
              >
                <div className={styles.detailIcon}>🎫</div>
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>
                    Номер на резервация
                  </span>
                  <span className={styles.detailValue}>#{reservationId}</span>
                </div>
              </div>
            )}
          </div>

          {/* Customer Information */}
          {currentUser && (
            <div className={styles.customerCard}>
              <h3 className={styles.customerTitle}>Информация за клиента</h3>
              <div className={styles.customerDetails}>
                <div className={styles.customerInfo}>
                  <span className={styles.customerLabel}>Име:</span>
                  <span className={styles.customerValue}>
                    {selectedFamilyMember
                      ? selectedFamilyMember.name
                      : `${currentUser.firstName} ${currentUser.lastName}`}
                  </span>
                </div>
                {(selectedFamilyMember
                  ? selectedFamilyMember.phoneNumber
                  : currentUser.phoneNumber) && (
                  <div className={styles.customerInfo}>
                    <span className={styles.customerLabel}>Телефон:</span>
                    <span className={styles.customerValue}>
                      {selectedFamilyMember
                        ? selectedFamilyMember.phoneNumber
                        : currentUser.phoneNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prepayment Notice */}
      {showDetails && requirePrepayment && !isWaitlistBooking && (
        <div className={styles.prepaymentNotice}>
          <div className={styles.prepaymentNoticeIcon}>💳</div>
          <div className={styles.prepaymentNoticeContent}>
            <h4 className={styles.prepaymentNoticeTitle}>Изисква се плащане</h4>
            <p className={styles.prepaymentNoticeText}>
              Този клас изисква предварително плащане.
            </p>

            <div className={styles.prepaymentInstructions}>
              <div className={styles.instructionsIcon}>⚠️</div>
              <div className={styles.instructionsContent}>
                <p className={styles.instructionsTitle}>Важно:</p>
                <p className={styles.instructionsText}>
                  За основание на плащането, моля въведете:{" "}
                  <strong className={styles.paymentReference}>
                    {paymentReference}
                  </strong>
                </p>
              </div>
            </div>

            {prepaymentLink && (
              <a
                href={prepaymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.prepaymentButton}
              >
                <span className={styles.prepaymentButtonIcon}>💳</span>
                <span className={styles.prepaymentButtonText}>Плати сега</span>
              </a>
            )}

            <p className={styles.prepaymentConfirmationText}>
              След успешно плащане, резервацията ще бъде{" "}
              <strong>потвърдена в рамките на няколко часа</strong> и ще
              получите имейл с потвърждение.
            </p>
          </div>
        </div>
      )}

      {/* Form Completion Notice */}
      {showDetails && requireFormCompletion && !isWaitlistBooking && (
        <div className={styles.formCompletionNotice}>
          <div className={styles.formCompletionNoticeIcon}>📋</div>
          <div className={styles.formCompletionNoticeContent}>
            <h4 className={styles.formCompletionNoticeTitle}>
              Изисква се попълване на формуляр
            </h4>
            <p className={styles.formCompletionNoticeText}>
              Този клас изисква попълване на задължителна форма.
            </p>

            {formCompletionLink && (
              <a
                href={formCompletionLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.formCompletionButton}
              >
                <span className={styles.formCompletionButtonIcon}>📝</span>
                <span className={styles.formCompletionButtonText}>
                  Попълни формуляр
                </span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Important Reminders */}
      {showDetails && (
        <div
          className={classNames(styles.reminders, {
            [styles.waitlistReminders]: isWaitlistBooking,
          })}
        >
          <h3 className={styles.remindersTitle}>Важна информация</h3>
          <div className={styles.remindersList}>
            {isWaitlistBooking ? (
              <>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>📧</span>
                  <span className={styles.reminderText}>
                    Ще получите имейл, когато се освободи място за този час
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⏳</span>
                  <span className={styles.reminderText}>
                    Позицията ви в списъка на чакащите е запазена
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⚡️</span>
                  <span className={styles.reminderText}>
                    Можете да управлявате резервациите си в "Моите резервации"
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⏰</span>
                  <span className={styles.reminderText}>
                    Моля, пристигнете поне 5 минути преди началото на класа
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>📱</span>
                  <span className={styles.reminderText}>
                    Получихте Email потвърждение за вашата резервация
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⚡️</span>
                  <span className={styles.reminderText}>
                    Можете да управлявате резервациите си в "Моите резервации"
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {showDetails && (
        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            {!isWaitlistBooking ? (
              <AddToCalendarButton
                reservationId={reservationId}
                className={styles.primaryButton}
              />
            ) : (
              <button
                className={styles.primaryButton}
                onClick={handleBookAnother}
                type="button"
              >
                <span className={styles.buttonIcon}>📅</span>
                <span className={styles.buttonText}>Резервирай друг клас</span>
              </button>
            )}

            <button
              className={styles.secondaryButton}
              onClick={handleGoToDashboard}
              type="button"
            >
              <span className={styles.buttonIcon}>📊</span>
              <span className={styles.buttonText}>Моите резервации</span>
            </button>
          </div>
        </div>
      )}
      <NotificationPermissionDialog
        isConfirmedReservation={!isWaitlistBooking && !requirePrepayment}
        studioName={selectedStudio?.name}
        eventDate={selectedDate}
        startTime={selectedTime}
      />
    </div>
  );
};
